import { getApiClient } from './authApiClient';
import { isSyntheticTrainingTenant } from '../config/syntheticTraining';

const mismatch = () => new Error('The server did not confirm this synthetic training account and scope. Reload before continuing.');
const nonempty = value => typeof value === 'string' && value.trim().length > 0;
const money = value => value && typeof value.amount === 'string' && /^\d+\.\d{2}$/.test(value.amount) && /^[A-Z]{3}$/.test(value.currency);
export function validateTrainingCompany(value, tenantId, companyId) {
  if (!value || value.tenantId !== tenantId || !nonempty(value.companyId) || (companyId && value.companyId !== companyId)
    || value.synthetic !== true || value.analysisOnly !== true || value.assessmentStatus !== 'not_assessed'
    || !nonempty(value.name) || !/^DEMO .+/.test(value.name) || !/^[A-Z]{2}$/.test(value.country)
    || !nonempty(value.sourceRevision) || !['requested', 'approved', 'insured'].every(key => money(value.limits?.[key]))
    || value.limits.requested.currency !== value.limits.approved.currency || value.limits.requested.currency !== value.limits.insured.currency) throw mismatch();
  return value;
}

export function createSyntheticTrainingApi(tenantId, principalId) {
  if (!isSyntheticTrainingTenant(tenantId) || !nonempty(principalId)) throw mismatch();
  const client = getApiClient('company');
  const base = `/synthetic-training/tenants/${encodeURIComponent(tenantId)}`;
  const request = async (method, suffix, payload, options = {}) => {
    const response = method === 'get'
      ? await client.get(`${base}${suffix}`, { timeout: 15000, ...options })
      : await client.post(`${base}${suffix}`, payload, { timeout: 30000, ...options, _retry: true });
    if (response.data?.success === false) throw new Error(response.data.message || 'The training operation was not confirmed.');
    return response.data?.data;
  };
  const validateCapabilities = value => {
    if (!value || value.tenantId !== tenantId || value.principalId !== principalId || value.synthetic !== true
      || !['initialized', 'active', 'canActivate', 'canCreate', 'canRead', 'canPreview', 'canUseFinancialReports'].every(key => typeof value[key] === 'boolean')
      || value.canSend !== false) throw mismatch();
    return value;
  };
  const companyPath = companyId => {
    if (!nonempty(companyId)) throw mismatch();
    return `/companies/${encodeURIComponent(companyId)}`;
  };
  return {
    capabilities: async options => validateCapabilities(await request('get', '/capabilities', undefined, options)),
    activate: async (requestId, options) => validateCapabilities(await request('post', '/activate', { requestId, confirmedSyntheticOnly: true }, options)),
    companies: async options => {
      const result = await request('get', '/companies', undefined, options);
      if (!Array.isArray(result)) throw mismatch();
      return result.map(row => validateTrainingCompany(row, tenantId));
    },
    createCompany: async (payload, options) => validateTrainingCompany(await request('post', '/companies', payload, options), tenantId),
    company: async (companyId, options) => validateTrainingCompany(await request('get', companyPath(companyId), undefined, options), tenantId, companyId),
    preview: async (companyId, options) => {
      const value = await request('post', `${companyPath(companyId)}/request-preview`, {}, options);
      if (!value || value.tenantId !== tenantId || value.companyId !== companyId || value.principalId !== principalId
        || value.synthetic !== true || value.eligible !== true || value.canSend !== false || value.canEditDraft !== true || value.canPreviewCarrierRequest !== true
        || !nonempty(value.sourceRevision) || !['requested', 'approved', 'insured'].every(key => money(value.limits?.[key]))
        || value.limits.requested.currency !== value.limits.approved.currency || value.limits.requested.currency !== value.limits.insured.currency) throw mismatch();
      return value;
    },
    // There is deliberately no send, provider lookup or normal company adapter.
  };
}
